import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { ChevronsUpDownIcon, CopyIcon, DicesIcon, EyeIcon, EyeOffIcon, XIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useRevalidateOnLanguageChange } from '@/i18n/use-revalidate-on-language-change';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { getRoleBadgeColor, type RoleType } from '@/common/permission';
import { ROLE } from '@/common/role';
import type { User } from '@/common/user';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Field, FieldDescription, FieldError, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { usePermission, usePermissionMetadata } from '@/hooks/use-permission';
import { getListOrganizationsOption } from '@/queries/organizations/list-organizations';
import { getListRolesOption } from '@/queries/permissions/list-roles';
import { addUserOptions } from '@/queries/users/add-user';
import { updateUserOptions } from '@/queries/users/update-user';
import { SelectOrganizationPopover } from '../../Organizations/SelectOrganizationPopup';

const PASSWORD_SETUP_METHOD = {
	EMAIL: 'EMAIL',
	TEMPORARY: 'TEMPORARY',
} as const;

const buildUserSchema = (t: TFunction) =>
	z
		.object({
			firstName: z.string().min(1, t('admin.users.validation.firstNameRequired')),
			lastName: z.string().min(1, t('admin.users.validation.lastNameRequired')),
			email: z.email(t('admin.users.validation.emailInvalid')),
			organizationId: z.string().min(1, t('admin.users.validation.organizationRequired')),
			roles: z.set(z.string()).min(1, t('admin.users.validation.rolesRequired')),
			tempPassword: z.string().min(8, t('admin.users.validation.passwordMinLength')).or(z.literal('')),
			passwordSetupMethod: z.enum([PASSWORD_SETUP_METHOD.EMAIL, PASSWORD_SETUP_METHOD.TEMPORARY]),
		})
		.superRefine((data, ctx) => {
			if (data.passwordSetupMethod === PASSWORD_SETUP_METHOD.TEMPORARY && !data.tempPassword) {
				ctx.addIssue({
					code: 'custom',
					message: t('admin.users.validation.temporaryPasswordRequired'),
					path: ['tempPassword'],
				});
			}
		});
type UserFormValues = z.infer<ReturnType<typeof buildUserSchema>>;

interface UserDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onUserChange: () => void;
	user: User | null;
}

export function UserDrawer({ open, onOpenChange, user, onUserChange }: UserDrawerProps) {
	const { t } = useTranslation();
	const userFormSchema = useMemo(() => buildUserSchema(t), [t]);
	const userForm = useForm<UserFormValues>({
		resolver: zodResolver(userFormSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			organizationId: '',
			roles: new Set(),
			passwordSetupMethod: PASSWORD_SETUP_METHOD.EMAIL,
			tempPassword: '',
		},
		mode: 'all',
	});
	useRevalidateOnLanguageChange(userForm);
	const userFormId = useId();
	const [showPassword, setShowPassword] = useState(false);
	const { isSuperAdmin, organizations: userOrgs } = usePermissionMetadata();
	const hasOrgPermission = usePermission(ROLE.ORGANIZATION_READ);
	const { data: orgListResponse } = useQuery(getListOrganizationsOption({ enabled: hasOrgPermission }));
	const organizations = orgListResponse?.organizations || [];
	const { data: roleListResponse } = useQuery(getListRolesOption());
	const roles = roleListResponse?.roles || [];
	const rolesById = roles.reduce(
		(acc, role) => {
			acc[role.id as string] = role;
			return acc;
		},
		{} as Record<string, RoleType>,
	);

	const addUserApi = useMutation(addUserOptions());
	const updateUserApi = useMutation(updateUserOptions());
	const selectedPaymentMethod = userForm.watch('passwordSetupMethod');
	useEffect(() => {
		if (user) {
			userForm.reset({
				firstName: user.firstName || '',
				lastName: user.lastName || '',
				email: user.email || '',
				organizationId: user.organizations[0]?.id || '',
				roles: new Set(user.groups),
				passwordSetupMethod: PASSWORD_SETUP_METHOD.EMAIL,
				tempPassword: '',
			});
		} else {
			userForm.reset({
				firstName: '',
				lastName: '',
				email: '',
				organizationId: isSuperAdmin ? '' : userOrgs[0]?.id || '',
				roles: new Set(),
				passwordSetupMethod: PASSWORD_SETUP_METHOD.EMAIL,
				tempPassword: '',
			});
		}
	}, [user, userForm.reset, userOrgs, isSuperAdmin]);

	const handleSubmit = async (values: UserFormValues) => {
		if (!values.firstName.trim() || !values.lastName.trim() || !values.email.trim()) {
			toast.error(t('admin.users.validation.requiredFields'));
			return;
		}
		// Updating User
		if (user) {
			await updateUserApi.mutateAsync({
				id: user.id as string,
				payload: {
					firstName: values.firstName.trim(),
					lastName: values.lastName.trim(),
					email: values.email.trim(),
					groups: Array.from(values.roles),
					organizations: values.organizationId ? [values.organizationId] : [],
				},
			});
		}
		// Creating User
		if (!user) {
			await addUserApi.mutateAsync({
				firstName: values.firstName.trim(),
				lastName: values.lastName.trim(),
				email: values.email.trim(),
				organizations: [values.organizationId as string],
				groups: Array.from(values.roles),
				// empty password will trigger email setup flow, temp password will be used if provided
				password: values.tempPassword || undefined,
			});
		}
		onUserChange();
		handleClose();
	};

	const handleClose = () => {
		userForm.reset({
			firstName: '',
			lastName: '',
			email: '',
			organizationId: '',
			roles: new Set(),
			passwordSetupMethod: PASSWORD_SETUP_METHOD.EMAIL,
			tempPassword: '',
		});
		onOpenChange(false);
	};

	const handleToggleRole = (role: string) => {
		const newSelected = new Set(userForm.getValues('roles'));
		if (newSelected.has(role)) {
			newSelected.delete(role);
		} else {
			newSelected.add(role);
		}
		userForm.setValue('roles', newSelected, { shouldValidate: true });
	};

	const handleRemoveRole = (role: string) => {
		const newSelected = new Set(userForm.getValues('roles'));
		newSelected.delete(role);
		userForm.setValue('roles', newSelected, { shouldValidate: true });
	};

	const generateRandomPassword = () => {
		const length = 12;
		const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
		let password = '';
		for (let i = 0; i < length; i++) {
			password += charset.charAt(Math.floor(Math.random() * charset.length));
		}
		userForm.setValue('tempPassword', password);
		setShowPassword(true);
	};

	const copyTempPasswordToClipboard = () => {
		const tempPassword = userForm.getValues('tempPassword');
		if (tempPassword) {
			navigator.clipboard.writeText(tempPassword);
			toast.success(t('admin.users.password.copied'));
		}
	};

	return (
		<Drawer direction={'right'} open={open} onOpenChange={onOpenChange}>
			<DrawerContent className="w-full max-w-md">
				{/* Header */}
				<DrawerHeader className="flex flex-row justify-between border-b border-zinc-300">
					<DrawerTitle>{user ? t('admin.users.form.editTitle') : t('admin.users.create')}</DrawerTitle>
					<DrawerClose asChild>
						<Button
							variant="ghost"
							size="icon-lg"
							type="button"
							className="text-muted-foreground transition-colors hover:bg-transparent"
						>
							<XIcon className="size-5" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				{/* Content */}
				<div className="no-scrollbar overflow-y-auto px-6 py-6">
					<form noValidate id={userFormId} onSubmit={userForm.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
						{/* Name */}
						<Controller
							name="firstName"
							control={userForm.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('admin.users.form.firstName')} <span className="text-red-600">*</span>
									</FieldLabel>
									<Input
										{...field}
										id={field.name}
										aria-invalid={fieldState.invalid}
										placeholder={t('admin.users.form.firstNamePlaceholder')}
										autoComplete="off"
										className="bg-input"
										required
									/>
									<FieldError errors={[fieldState.error]} />
								</Field>
							)}
						/>
						<Controller
							name="lastName"
							control={userForm.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('admin.users.form.lastName')} <span className="text-red-600">*</span>
									</FieldLabel>
									<Input
										{...field}
										id={field.name}
										aria-invalid={fieldState.invalid}
										placeholder={t('admin.users.form.lastNamePlaceholder')}
										autoComplete="off"
										className="bg-input"
										required
									/>
									<FieldError errors={[fieldState.error]} />
								</Field>
							)}
						/>

						{/* Email */}
						<Controller
							name="email"
							control={userForm.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('common.labels.email')} <span className="text-red-600">*</span>
									</FieldLabel>
									<Input
										{...field}
										type="email"
										id={field.name}
										aria-invalid={fieldState.invalid}
										placeholder={t('admin.users.form.emailPlaceholder')}
										autoComplete="off"
										className="bg-input"
										required
									/>
									<FieldError errors={[fieldState.error]} />
								</Field>
							)}
						/>

						{/* Password Setup Method */}
						{!user && (
							<Controller
								name="passwordSetupMethod"
								control={userForm.control}
								render={({ field, fieldState }) => (
									<FieldSet className="w-full">
										<FieldLegend variant="label">
											{t('admin.users.form.passwordSetupMethod')} <span className="text-red-600">*</span>
										</FieldLegend>
										<RadioGroup className="space-y-2 gap-1" value={field.value} onValueChange={field.onChange}>
											<Field
												key={PASSWORD_SETUP_METHOD.EMAIL}
												data-invalid={fieldState.invalid}
												orientation="horizontal"
												className="border-input has-data-[state=checked]:border-primary/50 relative flex flex-row w-full items-center gap-2 rounded-md border p-4 shadow-xs outline-none"
											>
												<RadioGroupItem
													value={PASSWORD_SETUP_METHOD.EMAIL}
													id={`password-setup-${PASSWORD_SETUP_METHOD.EMAIL}`}
													aria-label={`password-setup-${PASSWORD_SETUP_METHOD.EMAIL}`}
													aria-describedby={`password-setup-${PASSWORD_SETUP_METHOD.EMAIL}-description`}
													className="size-5 after:absolute after:inset-0 [&_svg]:size-3"
												/>
												<div className="grid grow gap-1">
													<FieldLabel
														htmlFor={`password-setup-${PASSWORD_SETUP_METHOD.EMAIL}`}
														className="justify-between"
													>
														{t('admin.users.form.sendPasswordResetEmail')}
													</FieldLabel>
													<FieldDescription
														id={`password-setup-${PASSWORD_SETUP_METHOD.EMAIL}-description`}
														className="text-xs"
													>
														{t('admin.users.form.sendPasswordResetEmailDescription')}
													</FieldDescription>
												</div>
											</Field>
											<Field
												key={PASSWORD_SETUP_METHOD.TEMPORARY}
												data-invalid={fieldState.invalid}
												orientation="horizontal"
												className="border-input has-data-[state=checked]:border-primary/50 relative flex flex-row w-full items-center gap-2 rounded-md border p-4 shadow-xs outline-none"
											>
												<RadioGroupItem
													value={PASSWORD_SETUP_METHOD.TEMPORARY}
													id={`password-setup-${PASSWORD_SETUP_METHOD.TEMPORARY}`}
													aria-label={`password-setup-${PASSWORD_SETUP_METHOD.TEMPORARY}`}
													aria-describedby={`password-setup-${PASSWORD_SETUP_METHOD.TEMPORARY}-description`}
													className="size-5 after:absolute after:inset-0 [&_svg]:size-3"
												/>
												<div className="grid grow gap-1">
													<FieldLabel
														htmlFor={`password-setup-${PASSWORD_SETUP_METHOD.TEMPORARY}`}
														className="justify-between"
													>
														{t('admin.users.form.setTemporaryPassword')}
													</FieldLabel>
													<FieldDescription
														id={`password-setup-${PASSWORD_SETUP_METHOD.TEMPORARY}-description`}
														className="text-xs"
													>
														{t('admin.users.form.setTemporaryPasswordDescription')}
													</FieldDescription>
												</div>
											</Field>
										</RadioGroup>
									</FieldSet>
								)}
							/>
						)}

						{/* Temporary Password Field - Only shown when temporary method is selected */}
						{!user && selectedPaymentMethod === PASSWORD_SETUP_METHOD.TEMPORARY && (
							<Controller
								name="tempPassword"
								control={userForm.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid}>
										<FieldLabel htmlFor={field.name} className="flex flex-row justify-between">
											<div>
												{t('admin.users.form.temporaryPassword')} <span className="text-red-600">*</span>
											</div>
											<button
												type="button"
												onClick={generateRandomPassword}
												className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 transition-colors"
												title={t('admin.users.form.generatePasswordTitle')}
											>
												<DicesIcon className="h-3.5 w-3.5" />
												<span>{t('admin.users.form.generate')}</span>
											</button>
										</FieldLabel>
										<div className="relative flex content-between">
											<Input
												{...field}
												id={field.name}
												type={showPassword ? 'text' : 'password'}
												aria-invalid={fieldState.invalid}
												placeholder={t('admin.users.form.temporaryPasswordPlaceholder')}
												autoComplete="off"
												required={selectedPaymentMethod === PASSWORD_SETUP_METHOD.TEMPORARY}
												className="bg-input"
											/>
											<div className="absolute top-0 right-0 flex">
												{field.value && (
													<Button
														className="hover:bg-transparent! text-muted-foreground hover:text-accent-foreground"
														onClick={copyTempPasswordToClipboard}
														size="icon"
														type="button"
														variant="ghost"
														title={t('admin.users.form.copyPasswordTitle')}
													>
														<CopyIcon className="size-4" />
													</Button>
												)}
												<Button
													className="hover:bg-transparent! text-muted-foreground hover:text-accent-foreground"
													onClick={() => setShowPassword(!showPassword)}
													size="icon"
													type="button"
													variant="ghost"
												>
													{showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
												</Button>
											</div>
										</div>
										<FieldDescription className="text-xs">
											{t('admin.users.form.temporaryPasswordHint')}
										</FieldDescription>
										<FieldError errors={[fieldState.error]} />
									</Field>
								)}
							/>
						)}

						{/* Organization Dropdown - Only show if user has permission to view organizations */}
						{hasOrgPermission && (
							<Controller
								name="organizationId"
								control={userForm.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid}>
										<FieldLabel htmlFor={field.name}>
											{t('common.labels.organization')} <span className="text-red-600">*</span>
										</FieldLabel>
										<SelectOrganizationPopover
											organizations={organizations}
											value={field.value}
											onChange={field.onChange}
										/>
										<FieldError errors={[fieldState.error]} />
									</Field>
								)}
							/>
						)}

						{/* Roles Multi-Select Dropdown */}
						<Controller
							name="roles"
							control={userForm.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('common.labels.roles')} <span className="text-red-600">*</span>
									</FieldLabel>
									<Popover>
										<PopoverTrigger asChild>
											<Button
												type="button"
												variant="outline"
												className="bg-input border text-foreground text-sm rounded px-2 py-1 focus:ring-1 focus:ring-blue-600 outline-none w-50 justify-between"
											>
												<span className={field.value.size > 0 ? 'text-foreground' : 'text-muted-foreground'}>
													{field.value.size > 0
														? t('admin.users.form.rolesSelected', { count: field.value.size })
														: t('admin.users.form.selectRoles')}
												</span>
												<ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="p-0 w-(--radix-popover-trigger-width)">
											<Command>
												<CommandInput placeholder={t('admin.users.form.searchRoles')} />
												<CommandList>
													<CommandEmpty>
														{roles.length === field.value.size
															? t('admin.users.form.allRolesSelected')
															: t('admin.users.form.noRolesFound')}
													</CommandEmpty>
													<CommandGroup>
														{roles.map((role) => (
															<CommandItem
																key={role.id}
																onSelect={() => {
																	handleToggleRole(role.id as string);
																}}
																value={role.id}
																keywords={[role.name || '']}
															>
																<Checkbox className="text-white" checked={field.value.has(role.id as string)} />
																{role.name}
															</CommandItem>
														))}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
									{/* Selected Roles */}
									{field.value.size > 0 && (
										<div className="mt-2 flex flex-wrap gap-2">
											{Array.from(field.value).map((role) => (
												<div
													key={role}
													className={`inline-flex items-center gap-1 px-2 py-1 text-sm rounded border ${getRoleBadgeColor(rolesById[role as string]?.name || '')}`}
												>
													<span>{rolesById[role]?.name || ''}</span>
													<button type="button" onClick={() => handleRemoveRole(role)} className="hover:opacity-70">
														<XIcon className="size-3" />
													</button>
												</div>
											))}
										</div>
									)}
									<FieldError errors={[fieldState.error]} />
								</Field>
							)}
						/>
					</form>
				</div>
				<DrawerFooter className="flex flex-row items-center justify-end gap-3 px-6 py-4 border-t">
					<DrawerClose asChild>
						<Button type="button" variant="outline" onClick={handleClose}>
							{t('common.actions.cancel')}
						</Button>
					</DrawerClose>
					<Button
						type="submit"
						form={userFormId}
						disabled={
							!userForm.formState.isValid ||
							userForm.formState.disabled ||
							addUserApi.isPending ||
							updateUserApi.isPending
						}
					>
						{addUserApi.isPending || updateUserApi.isPending ? t('common.states.saving') : t('common.actions.save')}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
}
